import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { LoadingOverlayService } from '../services/loading-overlay.service';
import { AuthDataService } from '../auth/auth-data.service';
import { stopPropagation } from 'ngx-scrollbar/lib/utils/common';
import { observable } from 'core-js/fn/symbol';

@Injectable()
export class HttpHandler {

  constructor(
    private router: Router,
    private http: HttpClient,
    private authDataService: AuthDataService,
    private loaderService: LoadingOverlayService) {
  }

  //#region Métodos HTTP
  get(rota: string, allowAnonymous: boolean = false, ignoreLoader = false): Observable<any> {
    if (!ignoreLoader) { this.loaderService.show(); }
    return this.http
      .get<any>(rota, { headers: this.headerGet(allowAnonymous) })
      .pipe(
        map(query => query),
        tap(_ => { if (!ignoreLoader) { this.loaderService.hide() } }),
        catchError((error: any) => this.handleError(error)));
  }
  getCustomHeader(rota: string, customHeaders: Array<{name:string , value:string}>,  allowAnonymous: boolean = false, ignoreLoader = false): Observable<any> {
    const headers = this.headerGet(allowAnonymous)
    customHeaders.forEach(e => {
      headers.append(e.name,e.value)
    });

    if (!ignoreLoader) { this.loaderService.show(); }
    return this.http
      .get<any>(rota, { headers: headers })
      .pipe(
        map(query => query),
        tap(_ => { if (!ignoreLoader) { this.loaderService.hide() } }),
        catchError((error: any) => this.handleError(error)));
  }

  getBlob(rota: string, allowAnonymous: boolean = false, ignoreLoader = false): Observable<any> {
    if (!ignoreLoader) { this.loaderService.show(); }
    return this.http
      .get<any>(rota, { responseType: 'blob' as 'json', headers: this.headerGet(allowAnonymous) })
      .pipe(
        map(query => query),
        tap(_ => { if (!ignoreLoader) { this.loaderService.hide() } }),
        catchError((error: any) => this.handleError(error)));
  }

  getHtml(rota: string, withoutHeader: boolean = false): Observable<any> {
    this.loaderService.show();
    return this.http
      .get(rota, { headers: (!withoutHeader ? this.headerGet() : null) })
      .pipe(
        tap(_ => { this.loaderService.hide() }),
        catchError((error: any) => this.handleError(error)));
  }

  post(rota: string, body: any = {}, ignoreLoader = false): Observable<any> {
    if (!ignoreLoader) { this.loaderService.show(); }
    return this.http
      .post<any>(rota, body, { headers: this.headerPost() })
      .pipe(
        map(query => query),
        tap(_ => { if (!ignoreLoader) { this.loaderService.hide() } }),
        catchError((error: any) => this.handleError(error)));
  }

  postCustomHeader(rota: string, body: any = {}, headers: any, ignoreLoader = false): Observable<any> {
    if (!ignoreLoader) { this.loaderService.show(); }
    return this.http
      .post<any>(rota, body, { headers: headers })
      .pipe(
        map(query => query),
        tap(_ => { if (!ignoreLoader) { this.loaderService.hide() } }),
        catchError((error: any) => this.handleError(error)));
  }

  put(rota: string, body: any): Observable<any> {
    this.loaderService.show();
    return this.http
      .put<any>(rota, body, { headers: this.headerPost() })
      .pipe(
        map(query => query),
        tap(_ => { this.loaderService.hide() }),
        catchError((error: any) => this.handleError(error)));
  }

  delete(rota: string): Observable<any> {
    this.loaderService.show();
    return this.http
      .delete<any>(rota, { headers: this.headerPost() })
      .pipe(
        map(query => query),
        tap(_ => { this.loaderService.hide() }),
        catchError((error: any) => this.handleError(error)));
  }

  downloadFile(rota: string, body: any, bypassHeader = false): Observable<any> {
    this.loaderService.show();
    return this.http
      .post(rota, body, (bypassHeader ? { responseType: 'blob' } : { headers: this.headerBlob(), responseType: 'blob' }))
      .pipe(
        tap(_ => { this.loaderService.hide() }),
        catchError((error: any) => this.handleError(error)));
  }

  //#endregion

  //#region Headers para os requests
  // header para request Get padrão
  private headerGet(allowAnonymous: boolean = false): HttpHeaders {
    if (allowAnonymous) {
      return new HttpHeaders({ Accept: 'application/json' });
    } else {
      return new HttpHeaders({ Authorization: `Bearer ${this.authDataService.usuario.token}`, Accept: 'application/json' });
    }
  }

  // header para Get com retorno HTML
  private headerGetHtml(): HttpHeaders {
    if (this.authDataService.usuario.isAuth) {
      const headers = new HttpHeaders({ Accept: 'text/html' });

      return headers;
    }
  }

  private headerPost(): HttpHeaders {
    if (this.authDataService.usuario.isAuth) {
      const headers = new HttpHeaders(
        {
          Authorization: `Bearer ${this.authDataService.usuario.token}`,
          Accept: 'application/json', 'Content-Type': 'application/json'
        });

      return headers;
    }
  }

  private headerBlob(): HttpHeaders {
    if (this.authDataService.usuario.isAuth) {
      const headers = new HttpHeaders(
        {
          Authorization: `${this.authDataService.usuario.token}`,
          Accept: 'application/octet-stream', 'Content-Type': 'application/json'
        });

      return headers;
    }
  }

  //#endregion

  //#region Tratamento de erro

  public handleError(erro: HttpErrorResponse | any): Observable<never> {
    let mensagemErro: string;
    if (erro instanceof HttpErrorResponse) {
      if (erro.status !== undefined) {
        if (erro.status === 401) {
          this.router.navigate(['/pages/login']);

          this.loaderService.hide();
          return new Observable();
        } else
          if (erro.status === 404) {
            mensagemErro = 'Ponto de acesso não encontrado.';
          } else {
            mensagemErro = erro.error.message;
            if (!mensagemErro) {
              mensagemErro = erro.error.data;
            }
            if (!mensagemErro) {
              mensagemErro = erro.message;
            }
            if (!mensagemErro) {
              mensagemErro = erro.error;
            }
            if (!mensagemErro) {
              mensagemErro = erro.toString();
            }
          }
      }
      this.loaderService.hide();
      return throwError(mensagemErro); //TODO deprecated

    } else {
      this.loaderService.hide();
      mensagemErro = erro.message ? erro.message : erro.toString();

      return throwError(mensagemErro); //TODO deprecated
    }
  }
  //#endregion
}
